import React from 'react';
import PropTypes from 'prop-types';
import './AiAssistant.css';
import StarImg from 'assets/icons/ai.svg';
import UploadIcon from 'assets/icons/upload-files.svg';
import { ROUTE_PATH, ROUTE_NAME } from 'utils/constants/router';
import { POPUP } from 'utils/constants/popup';
import { UNANSWERED_URL_SEARCH_PARAM } from 'utils/constants/urlSearchParameters';
import { useTranslation } from 'utils/modifiedTranslation';
import { useAIAssistant } from 'utils/hooks/useAIAssistant';
import Button from 'new-ui/Components/Button';
import { THEMES } from 'new-ui/constants';
import app from 'new-ui/app';
import { getQstack } from 'new-ui/Components/Intake/functions';
import { getFieldsUpdateDates } from 'modules/user/api';
import cx from 'classnames';
import { QUESTION_IDS_BY_DISEASE } from 'modules/questionnaire/constants';
import BuildingPanel from './assets/images/building-panel.svg';

export const AI_WIDGET_THEMES = {
  DARK: 'dark',
};

const AiAssistant = ({
  onUploadClick,
  onEditClick,
  theme,
  customUploadIcon,
  customUploadLabel,
}) => {
  const { t } = useTranslation();
  const { isAIAssistantShown } = useAIAssistant();

  return (
    <div className={cx('profile-header-actions', 'ai-assistant', theme)}>
      <div className="ai-image-wrapper">
        <img src={BuildingPanel} alt="building profile" />
      </div>
      <div className="title">{t('ai_profile.bar_title')}</div>
      {isAIAssistantShown && (
        <div className="ai-area">
          <div className="upload-nav">
            <div className="info">
              <img src={StarImg} alt="star" />
              <div>
                {t('ai_profile.ai_title')}
              </div>
            </div>
            <div className="buttons">
              <Button
                theme={THEMES.RUBY}
                action={() => {
                  if (onUploadClick) onUploadClick();
                  app.setPopup(POPUP.UPLOAD, {
                    theme: 'middle',
                    preventOverlayClose: true,
                    condition: app.user.personal.condition,
                    onGotoResultsAction: () => {
                      app.setPopup(false);
                    },
                    onBeforeIntake: async (questions, results, user) => {
                      // !!! user mutation
                      // eslint-disable-next-line no-param-reassign
                      user.condition_profile = Object.assign(app.user.condition_profile, user.condition_profile);
                      // eslint-disable-next-line no-param-reassign
                      user.info = Object.assign(app.user.info, user.info);
                      // eslint-disable-next-line no-param-reassign
                      user.personal = Object.assign(app.user.personal, user.personal);
                      // eslint-disable-next-line no-param-reassign
                      user.qstack = await getQstack(user, app.questions);

                      const fields = (await getFieldsUpdateDates()) ?? {};
                      const hasDate = results?.files?.find((a) => a.documentDate);
                      if (!hasDate) return;
                      if (!results?.files) return;
                      const fi = [];
                      results.files.forEach((file) => {
                        let fileDate = file.documentDate;
                        let num = 0;
                        if (!fileDate) return;
                        fileDate = new Date(file.documentDate);
                        Object.keys(fields).forEach((fieldName) => {
                          let adjustedFieldName = fieldName;
                          if (adjustedFieldName === 'patient_stage') {
                            const patientStageField = QUESTION_IDS_BY_DISEASE[app.user.personal.condition].patientStage;
                            adjustedFieldName = patientStageField;
                          }
                          const field = fields[adjustedFieldName];
                          const fieldDate = new Date(field.date);
                          if (fileDate < fieldDate) {
                            num += 1;
                            if (adjustedFieldName === 'patient_stage') questions.splice(questions.indexOf('current_stage'), 1);
                            else {
                              questions.splice(questions.indexOf(adjustedFieldName), 1);
                            }
                          }
                        });
                        if (num === Object.keys(fields).length) {
                          fi.push(file);
                        }
                      });
                      if (!questions.length) {
                        app.setPopup(false);
                        app.setPopup(POPUP.GENERAL, {
                          theme: 'middle',
                          title: t('ai_profile.profile_up_to_date'),
                          content: (
                            <div>
                              <Button
                                title={t('ai_profile.close')}
                                action={() => {
                                  app.setPopup(false);
                                }}
                              />
                            </div>
                          ),
                        });
                      }
                    },
                    onSuccess: async (extractedProfile) => {
                      Object.keys(app.user.condition_profile).forEach((field) => {
                        if (extractedProfile.condition_profile[field]) {
                          // !!! user mutation
                          app.user.condition_profile[field] = extractedProfile.condition_profile[field];
                        }
                      });
                      const qstack = await getQstack(app.user, app.questions);
                      // !!! user mutation
                      app.user.condition_profile.qstack = qstack;
                      app.showPreloader();
                      await app.handleUserUpdate();
                      app.hidePreloader();
                    },
                  });
                }}
                backgroundColor="#C77DFF"
                title={customUploadLabel || t('ai_profile.upload_button')}
                icon={customUploadIcon || UploadIcon}
                className="upload-files"
                width="210"
              />
            </div>
          </div>
        </div>
      )}
      <div className="intake-area">
        <Button
          className="intake-button"
          title={t('general.update_manually')}
          action={() => {
            app.setPopup(false);
            if (onEditClick) onEditClick();
            app.history.push(`${ROUTE_PATH[ROUTE_NAME.INTAKE]}?${UNANSWERED_URL_SEARCH_PARAM}=1`);
          }}
        />
      </div>
    </div>
  );
};

AiAssistant.propTypes = {
  onUploadClick: PropTypes.func,
  onEditClick: PropTypes.func,
  theme: PropTypes.string,
  customUploadIcon: PropTypes.string,
  customUploadLabel: PropTypes.string,
};

export default AiAssistant;
